import { useEffect, useState } from "react";
import { useFormState } from "react-hook-form";

import { isTiptapDocumentBlank } from "@/react/components/CommentsCompass/helpers";
import { useCommentsConfig } from "@/react/components/CommentsCompass/Provider/ConfigContext";
import type { TiptapFormValues } from "@/react/components/CommentsCompass/types";
import {
  clearPersistedFormFieldValues,
  usePersistedFormFieldValues,
  usePersistFormFields,
} from "@/react/hooks/utils/usePersistFormFields";

interface CommentTarget {
  parentCommentId?: number;
}

type PersistedDraft = Partial<TiptapFormValues>;

const useCommentDraftKey = ({ parentCommentId }: CommentTarget) => {
  const { postId } = useCommentsConfig();
  return parentCommentId
    ? `post-${postId}-comment-${parentCommentId}-reply-form-values`
    : `post-${postId}-comment-form-values`;
};

export const useStoredCommentDraft = ({ parentCommentId }: CommentTarget) => {
  const cacheKey = useCommentDraftKey({ parentCommentId });

  const { getPersistedFormFieldValues } =
    usePersistedFormFieldValues<PersistedDraft>({ cacheKey });

  const [storedDraft] = useState(() => {
    const tiptapBody = getPersistedFormFieldValues()?.tiptap_body;
    return isTiptapDocumentBlank(tiptapBody?.body) ? undefined : tiptapBody;
  });

  return { storedDraft };
};

export const usePersistCommentDraft = ({ parentCommentId }: CommentTarget) => {
  const { isDirty, isSubmitting, isSubmitSuccessful } = useFormState();
  const cacheKey = useCommentDraftKey({ parentCommentId });

  usePersistFormFields({
    cacheKey,
    fields: ["tiptap_body"],
    enabled: isDirty && !isSubmitting && !isSubmitSuccessful,
  });

  useEffect(() => {
    if (isSubmitSuccessful) clearPersistedFormFieldValues(cacheKey);
  }, [isSubmitSuccessful, cacheKey]);
};
